import type Vue from 'vue';

/**
 * TourList（tour-list.com）への送客計測用のリンク設定
 *
 * provide / prop で受け渡し、tour-list.com へのリンクにだけ作用する
 */
export interface TourListLinkOptions {
  /** ページ内の設置場所の識別子（utm_term） */
  term: string;
  /**
   * 商材の識別子（utm_campaign）
   *
   * パッケージ付きプランの欄のように、コード上で商材が確定しているリンクでのみ指定する。
   * CMS で自由に入力されるリンクは、CMS 側で URL に付与する
   */
  campaign?: string;
}

/**
 * provide のキー
 *
 * provide 側は計算プロパティ名にするとビルド後にヘルパー呼び出しになるため、`tourList` を直接書く
 */
export const TOUR_LIST_PROVIDE_KEY = 'tourList';

/** tour-list.com へのリンクに付与する referrerpolicy */
export const TOUR_LIST_REFERRER_POLICY = 'no-referrer-when-downgrade';

/** 設置場所ごとのリンク設定 */
export const TOUR_LIST_LINK_OPTIONS = Object.freeze({
  /** ページヘッダー（SP時はフッター）の予約ボタン */
  header: Object.freeze({ term: 'header' }),
  /** ヘッダーのお知らせ欄 */
  notification: Object.freeze({ term: 'notification' }),
  /** 空室検索の上部バナー */
  roomsearchBanner: Object.freeze({ term: 'roomsearch-banner' }),
  /** 空室検索のフッター */
  roomsearchFooter: Object.freeze({ term: 'roomsearch-footer' }),
  /** アクセスページ内 */
  accessLink: Object.freeze({ term: 'access-link' }),
  /** 特集ページ内 */
  feature: Object.freeze({ term: 'feature' }),
  /** 施設ページの動線バナー */
  banner: Object.freeze({ term: 'banner' }),
  /** 上記のいずれにも該当しない場所 */
  other: Object.freeze({ term: 'other' }),
});

/** パッケージ付きプランの欄ごとの商材の識別子 */
export const TOUR_LIST_CAMPAIGNS = Object.freeze({
  air: 'air',
  shinkansen: 'shinkansen',
  rentalCar: 'rentacar',
});

const campaignOptionsCache: { [key: string]: TourListLinkOptions } = {};

/**
 * 設置場所のリンク設定に商材を加えた設定を取得する
 *
 * 描画のたびに別オブジェクトにならないよう、同じ組み合わせでは同じオブジェクトを返す
 */
export function withTourListCampaign(
  options: TourListLinkOptions,
  campaign: string,
): TourListLinkOptions {
  const cacheKey = `${options.term}\n${campaign}`;
  let cached = campaignOptionsCache[cacheKey];
  if (!cached) {
    cached = Object.freeze({ term: options.term, campaign });
    campaignOptionsCache[cacheKey] = cached;
  }
  return cached;
}

const TOUR_LIST_URL_RE = /^(?:https?:)?\/\/(?:[^/?#@]+@)?(?:[^/?#@:]+\.)?tour-list\.com(?::\d+)?(?:[/?#]|$)/i;

const TOUR_LIST_TEXT_RE = /tour-list\.com/i;

const ANCHOR_START_TAG_RE = /<a\b(?:"[^"]*"|'[^']*'|[^'">])*>/gi;

function createAttrRe(name: string) {
  return new RegExp(
    `(\\s${name}\\s*=\\s*)(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`,
    'i',
  );
}

const HREF_ATTR_RE = createAttrRe('href');
const REL_ATTR_RE = createAttrRe('rel');
const REFERRER_POLICY_ATTR_RE = createAttrRe('referrerpolicy');

/**
 * tour-list.com（サブドメインを含む）へのURLかどうか
 */
export function isTourListHref(href?: string | null): href is string {
  return !!href && TOUR_LIST_URL_RE.test(href.trim());
}

/**
 * ページのパスから utm_content の値を生成する
 *
 * 先頭と末尾のスラッシュを除き、残るスラッシュをハイフンに置き換える
 */
export function toTourListContent(path: string) {
  return path.replace(/^\/+|\/+$/g, '').replace(/\/+/g, '-');
}

/**
 * コンポーネントツリーを遡って、provide されたリンク設定を取得する
 *
 * Vue 2 の inject の解決と同じ方法で `_provided` を探す。
 * 自身が provide している場合は自身の値を優先する。
 * どこにも provide されていなければ `other` を返す。
 */
export function resolveTourListLinkOptions(
  vm?: Vue | null,
): TourListLinkOptions {
  let source: any = vm;
  while (source) {
    const provided = source._provided;
    if (
      provided &&
      Object.prototype.hasOwnProperty.call(provided, TOUR_LIST_PROVIDE_KEY) &&
      provided[TOUR_LIST_PROVIDE_KEY]
    ) {
      return provided[TOUR_LIST_PROVIDE_KEY];
    }
    source = source.$parent;
  }
  return TOUR_LIST_LINK_OPTIONS.other;
}

/**
 * 現在のルートのパス
 */
function getCurrentPath(vm?: Vue | null): string {
  const route = vm && vm.$route;
  return (route && route.path) || '';
}

/**
 * tour-list.com へのURLに、まだ付いていない計測パラメータを末尾に追記する
 *
 * * 既存のパラメータ・表記は一切変更しない
 * * utm_campaign は、リンク設定で商材が指定されている場合のみ付与する（それ以外は CMS 側で付与する）
 *
 * @param href - 変換元のURL
 * @param options - リンク設定
 * @param path - リンク元ページのパス
 * @param separator - パラメータの区切り文字（HTML文字列内では `&amp;`）
 */
export function appendTourListParams(
  href: string,
  options: TourListLinkOptions,
  path: string,
  separator: string = '&',
): string {
  const hashIndex = href.indexOf('#');
  const base = hashIndex === -1 ? href : href.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : href.slice(hashIndex);
  const queryIndex = base.indexOf('?');
  const query = queryIndex === -1 ? '' : base.slice(queryIndex + 1);

  const existingKeys: string[] = [];
  query
    .replace(/&amp;/g, '&')
    .split('&')
    .forEach((pair) => {
      const key = pair.split('=')[0];
      if (!key) return;
      try {
        existingKeys.push(decodeURIComponent(key));
      } catch (_err) {
        existingKeys.push(key);
      }
    });

  const params: [string, string][] = [
    ['utm_source', 'hoshinoresorts'],
    ['utm_medium', 'referral'],
    ['utm_campaign', options.campaign || ''],
    ['utm_content', toTourListContent(path)],
    ['utm_term', options.term],
  ];

  const additions = params
    .filter(([key, value]) => !!value && !existingKeys.includes(key))
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`);

  if (!additions.length) return href;

  let joiner: string;
  if (queryIndex === -1) {
    joiner = '?';
  } else if (/[?&]$/.test(base) || base.endsWith('&amp;')) {
    joiner = '';
  } else {
    joiner = separator;
  }

  return `${base}${joiner}${additions.join(separator)}${hash}`;
}

/**
 * コンポーネントの文脈で、tour-list.com へのURLに計測パラメータを追記する
 *
 * tour-list.com 以外のURLはそのまま返す
 */
export function decorateTourListHref<T extends string | null | undefined>(
  href: T,
  vm?: Vue | null,
  options?: TourListLinkOptions,
): T {
  if (!isTourListHref(href)) return href;
  return appendTourListParams(
    href,
    options || resolveTourListLinkOptions(vm),
    getCurrentPath(vm),
  ) as T;
}

/**
 * rel から noreferrer を取り除く
 *
 * noreferrer は referrerpolicy より優先されるため、tour-list.com へのリンクでは外す
 */
export function removeNoReferrer(rel?: string | null): string | undefined {
  if (!rel) return undefined;
  const tokens = rel
    .split(/\s+/)
    .filter((token) => !!token && token.toLowerCase() !== 'noreferrer');
  return tokens.length ? tokens.join(' ') : undefined;
}

/**
 * 生の a タグの属性を、tour-list.com へのリンク用に変換する
 *
 * tour-list.com 以外のURLの場合は、渡された値をそのまま返す
 */
export function resolveTourListAnchorAttrs<
  H extends string | null | undefined,
  R extends string | null | undefined
>(
  href: H,
  rel: R,
  vm?: Vue | null,
  options?: TourListLinkOptions,
): {
  href: H;
  rel: R | string | undefined;
  referrerpolicy?: string;
} {
  if (!isTourListHref(href)) return { href, rel };
  return {
    href: decorateTourListHref(href, vm, options),
    rel: removeNoReferrer(rel),
    referrerpolicy: TOUR_LIST_REFERRER_POLICY,
  };
}

function readAttrValue(match: RegExpMatchArray): string {
  if (match[2] !== undefined) return match[2];
  if (match[3] !== undefined) return match[3];
  return match[4] || '';
}

function decorateAnchorStartTag(
  tag: string,
  options: TourListLinkOptions,
  getPath: () => string,
): string {
  const hrefMatch = tag.match(HREF_ATTR_RE);
  if (!hrefMatch) return tag;
  const href = readAttrValue(hrefMatch);
  if (!isTourListHref(href)) return tag;

  let result = tag;

  const decoratedHref = appendTourListParams(href, options, getPath(), '&amp;');
  if (decoratedHref !== href) {
    const nextHrefAttr = `${hrefMatch[1]}"${decoratedHref.replace(
      /"/g,
      '&quot;',
    )}"`;
    result = result.replace(hrefMatch[0], () => nextHrefAttr);
  }

  const relMatch = result.match(REL_ATTR_RE);
  if (relMatch) {
    const rel = readAttrValue(relMatch);
    const nextRel = removeNoReferrer(rel);
    if (nextRel !== rel) {
      const nextRelAttr = nextRel ? `${relMatch[1]}"${nextRel}"` : '';
      result = result.replace(relMatch[0], () => nextRelAttr);
    }
  }

  const policyMatch = result.match(REFERRER_POLICY_ATTR_RE);
  if (policyMatch) {
    if (readAttrValue(policyMatch) !== TOUR_LIST_REFERRER_POLICY) {
      const nextPolicyAttr = `${policyMatch[1]}"${TOUR_LIST_REFERRER_POLICY}"`;
      result = result.replace(policyMatch[0], () => nextPolicyAttr);
    }
  } else {
    result = result.replace(
      /^<a\b/i,
      `$& referrerpolicy="${TOUR_LIST_REFERRER_POLICY}"`,
    );
  }

  return result;
}

/**
 * HTML文字列にtour-list.comへのリンクが含まれている可能性があるか
 */
export function mayContainTourListLink(html?: string | null): boolean {
  return !!html && TOUR_LIST_TEXT_RE.test(html);
}

/**
 * HTML文字列内の tour-list.com への a タグを変換する
 *
 * * 計測パラメータの追記
 * * referrerpolicy の付与
 * * rel から noreferrer を除去
 *
 * tour-list.com 以外の a タグ、a タグ以外の部分は一切変更しない
 */
export function decorateTourListHtml(
  html: string,
  vm?: Vue | null,
  options?: TourListLinkOptions,
): string {
  if (!mayContainTourListLink(html)) return html;
  const resolvedOptions = options || resolveTourListLinkOptions(vm);
  const getPath = () => getCurrentPath(vm);
  return html.replace(ANCHOR_START_TAG_RE, (tag) =>
    decorateAnchorStartTag(tag, resolvedOptions, getPath),
  );
}
